import { createReducer } from 'src/helpers/reducer';
import { logger } from 'src/helpers/logger';
import { isProduction } from 'src/settings';
import actionTypes from './actionTypes';
import { OPTION_LISTS } from './constants';
import type { OptionListKey } from './constants';
import { deriveListScope, normaliseSearch } from './helpers';
import type {
  IFetchOptionsErrorPayload,
  IFetchOptionsRequestPayload,
  IFetchOptionsSuccessPayload,
  IFormData,
  IOptionListState,
  IRateSimulatorResult,
  IRateSimulatorState,
  IRunError,
} from './types';

const initListState: IOptionListState = {
  options: [],
  isLoading: false,
  hasError: false,
  hasLoaded: false,
  nextPage: null,
  search: '',
  scope: null,
};

const initLists = Object.values(OPTION_LISTS).reduce(
  (acc, key) => ({ ...acc, [key]: initListState }),
  {} as Record<OptionListKey, IOptionListState>,
);

const initState: IRateSimulatorState = {
  lists: initLists,
  formData: {
    isLoading: false,
    hasError: false,
    data: null,
  },
  run: {
    isLoading: false,
    error: null,
    result: null,
    isStale: false,
  },
};

const withList = (
  state: IRateSimulatorState,
  list: OptionListKey,
  next: Partial<IOptionListState>,
): IRateSimulatorState => ({
  ...state,
  lists: {
    ...state.lists,
    [list]: { ...state.lists[list], ...next },
  },
});

// A response answers the request whose search and scope it carries; anything else has been overtaken.
const isSuperseded = (payload: { search: string; scope: string | null }, current: IOptionListState): boolean =>
  payload.search !== current.search || payload.scope !== current.scope;

function onFetchOptionsRequest(
  state: IRateSimulatorState,
  { payload }: { payload: IFetchOptionsRequestPayload },
): IRateSimulatorState {
  // Derived through the helper the saga's reply also goes through: a rule applied on one side only
  // would make the guard below reject a correct reply.
  return withList(state, payload.list, {
    isLoading: true,
    hasError: false,
    search: normaliseSearch(payload.search),
    scope: deriveListScope(payload.list, payload.scopeId),
  });
}

function onFetchOptionsSuccess(
  state: IRateSimulatorState,
  { payload }: { payload: IFetchOptionsSuccessPayload },
): IRateSimulatorState {
  const current = state.lists[payload.list];

  // Nothing cancels an in-flight fetch, so an answer to a superseded search or scope is dropped.
  // Without the scope check, one unit's positions could land under — or be appended onto — another's.
  if (isSuperseded(payload, current)) {
    // Typing supersedes a response on most keystrokes, so this is a development signal rather than an
    // incident: what a divergence between the two derivations would look like, beside a stuck picker.
    if (!isProduction) {
      logger.warn(
        `[rateSimulator] dropped a superseded ${payload.list} response: search ` +
          `${JSON.stringify(payload.search)} vs ${JSON.stringify(current.search)}, scope ` +
          `${JSON.stringify(payload.scope)} vs ${JSON.stringify(current.scope)}`,
      );
    }

    return state;
  }

  return withList(state, payload.list, {
    options: payload.append ? [...current.options, ...payload.options] : payload.options,
    nextPage: payload.nextPage,
    isLoading: false,
    hasError: false,
    hasLoaded: true,
  });
}

function onFetchOptionsError(
  state: IRateSimulatorState,
  { payload }: { payload: IFetchOptionsErrorPayload },
): IRateSimulatorState {
  const current = state.lists[payload.list];

  // Guarded like the success: without it a superseded failure paints an error on a list that has
  // since loaded, leaving the picker red and full of correct options with nothing naming a cause.
  if (isSuperseded(payload, current)) return state;

  return withList(state, payload.list, { isLoading: false, hasError: true });
}

// A list that follows no pick is never cleared: nothing it holds can be answering a pick just left.
function onClearListOptions(state: IRateSimulatorState, { payload }: { payload: OptionListKey }): IRateSimulatorState {
  return withList(state, payload, initListState);
}

function onFetchFormDataRequest(state: IRateSimulatorState): IRateSimulatorState {
  return {
    ...state,
    formData: { ...state.formData, isLoading: true, hasError: false },
  };
}

function onFetchFormDataSuccess(state: IRateSimulatorState, { payload }: { payload: IFormData }): IRateSimulatorState {
  return {
    ...state,
    formData: { isLoading: false, hasError: false, data: payload },
  };
}

function onFetchFormDataError(state: IRateSimulatorState): IRateSimulatorState {
  return {
    ...state,
    // `data` stays null: without a boundary the page shows no parity rather than a guessed one.
    // Deliberately not surfaced anywhere — a missing suffix is not worth interrupting a run over.
    formData: { isLoading: false, hasError: true, data: null },
  };
}

function onRunRequest(state: IRateSimulatorState): IRateSimulatorState {
  return {
    ...state,
    run: { ...state.run, isLoading: true, error: null },
  };
}

function onRunSuccess(state: IRateSimulatorState, { payload }: { payload: IRateSimulatorResult }): IRateSimulatorState {
  return {
    ...state,
    run: { isLoading: false, error: null, result: payload, isStale: false },
  };
}

function onRunError(state: IRateSimulatorState, { payload }: { payload: IRunError }): IRateSimulatorState {
  return {
    ...state,
    // The previous result is dropped: an error never shows figures beside it.
    run: { ...state.run, isLoading: false, error: payload, result: null, isStale: false },
  };
}

function onMarkResultStale(state: IRateSimulatorState): IRateSimulatorState {
  if (!state.run.result || state.run.isStale) return state;

  return {
    ...state,
    run: { ...state.run, isStale: true },
  };
}

export default createReducer(initState, {
  [actionTypes.FETCH_OPTIONS_REQUEST]: onFetchOptionsRequest,
  [actionTypes.FETCH_OPTIONS_SUCCESS]: onFetchOptionsSuccess,
  [actionTypes.FETCH_OPTIONS_ERROR]: onFetchOptionsError,
  [actionTypes.CLEAR_LIST_OPTIONS]: onClearListOptions,
  [actionTypes.FETCH_FORM_DATA_REQUEST]: onFetchFormDataRequest,
  [actionTypes.FETCH_FORM_DATA_SUCCESS]: onFetchFormDataSuccess,
  [actionTypes.FETCH_FORM_DATA_ERROR]: onFetchFormDataError,
  [actionTypes.RUN_REQUEST]: onRunRequest,
  [actionTypes.RUN_SUCCESS]: onRunSuccess,
  [actionTypes.RUN_ERROR]: onRunError,
  [actionTypes.MARK_RESULT_STALE]: onMarkResultStale,
  [actionTypes.CLEAR_STORE]: () => initState,
});
